<template>
  <section id="hero" class="bg-black" data-section-name="Hero">
    <div class="relative isolate px-6 pt-14 lg:px-8">
      <div
        class="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
        aria-hidden="true"
      >
        <div
          class="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-gray-200 to-gray-400 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
          style="
            clip-path: polygon(
              74.1% 44.1%,
              100% 61.6%,
              97.5% 26.9%,
              85.5% 0.1%,
              80.7% 2%,
              72.5% 32.5%,
              60.2% 62.4%,
              52.4% 68.1%,
              47.5% 58.3%,
              45.2% 34.5%,
              27.5% 76.7%,
              0.1% 64.9%,
              17.9% 100%,
              27.6% 76.8%,
              76.1% 97.7%,
              74.1% 44.1%
            );
          "
        ></div>
      </div>
      <div class="mx-auto max-w-2xl py-32 sm:py-48 lg:py-56">
        <div class="hidden sm:mb-8 sm:flex sm:justify-center">
          <div
            class="relative rounded-full px-3 py-1 text-sm leading-6 text-gray-400 ring-1 ring-gray-600 hover:ring-gray-500"
          >
            Launching soon - Join our early access program.
            <a href="#about" @click="analytics.trackButtonClick('Learn More - About', 'Early Access Banner')" class="font-semibold text-white cursor-pointer"
              ><span class="absolute inset-0" aria-hidden="true"></span>Learn
              more <span aria-hidden="true">&rarr;</span></a
            >
          </div>
        </div>
        <div class="text-center">
          <h1 class="text-4xl font-bold tracking-tight text-white sm:text-6xl">
            Convert HTML to PDF with lightning speed
          </h1>
          <p class="mt-6 text-lg leading-8 text-gray-400" role="banner">
            PDFGen is a fast, developer-friendly API service that converts HTML
            or URLs into high-quality PDFs. Perfect for reports, invoices,
            receipts, and more.
          </p>
          <div class="mt-10 flex items-center justify-center gap-x-6">
            <div class="flex flex-col sm:flex-row items-center gap-4">
              <form
                @submit.prevent="handleEarlyAccess"
                class="flex flex-col sm:flex-row gap-2"
              >
                <input
                  v-model="email"
                  type="email"
                  placeholder="Enter your email"
                  required
                  class="rounded-md px-3.5 py-2.5 text-sm text-black shadow-sm ring-1 ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black min-w-[250px]"
                />
                <button
                  type="submit"
                  :disabled="isSubmitting"
                  class="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-black shadow-sm hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ isSubmitting ? "Joining..." : "Join Early Access" }}
                </button>
              </form>
            </div>
            <a
              href="#features"
              @click="analytics.trackButtonClick('Learn More - Features', 'Hero Section')"
              class="text-sm font-semibold leading-6 text-white cursor-pointer"
              >Learn more <span aria-hidden="true">→</span></a
            >
          </div>
          <div v-if="successMessage" class="mt-4">
            <p class="text-sm text-green-400">{{ successMessage }}</p>
          </div>
          <div v-if="errorMessage" class="mt-4">
            <p class="text-sm text-red-400">{{ errorMessage }}</p>
          </div>
        </div>
      </div>
      <div
        class="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
        aria-hidden="true"
      >
        <div
          class="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-gray-200 to-gray-400 opacity-20 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
          style="
            clip-path: polygon(
              74.1% 44.1%,
              100% 61.6%,
              97.5% 26.9%,
              85.5% 0.1%,
              80.7% 2%,
              72.5% 32.5%,
              60.2% 62.4%,
              52.4% 68.1%,
              47.5% 58.3%,
              45.2% 34.5%,
              27.5% 76.7%,
              0.1% 64.9%,
              17.9% 100%,
              27.6% 76.8%,
              76.1% 97.7%,
              74.1% 44.1%
            );
          "
        ></div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue";
import emailjs from "@emailjs/browser";
import { useAnalytics } from "../composables/useAnalytics";

const email = ref("");
const isSubmitting = ref(false);
const successMessage = ref("");
const errorMessage = ref("");
const analytics = useAnalytics();

const handleEarlyAccess = async () => {
  if (!email.value) {
    errorMessage.value = "Please enter a valid email address";
    analytics.trackFormSubmission('Early Access Form', false);
    return;
  }

  isSubmitting.value = true;
  successMessage.value = "";
  errorMessage.value = "";

  try {
    // Option 1: EmailJS (client-side email sending)
    await emailjs.send(
      "pdfgen-wishlist", // Get from EmailJS dashboard
      "pdfgen-wishlist", // Get from EmailJS dashboard
      {
        email: email.value,
      },
      "_Z2fOrxU0df4A1n8i" // Get from EmailJS dashboard
    );

    successMessage.value =
      "Thanks for joining! We'll notify you when PDFGen launches.";
    email.value = "";
    
    // Track successful form submission
    analytics.trackFormSubmission('Early Access Form', true);
    analytics.trackEvent({
      action: 'signup',
      category: 'conversion',
      label: 'Early Access Email Signup',
      value: 1
    });
  } catch (error) {
    console.error("Error:", error);
    errorMessage.value = "Something went wrong. Please try again.";
    analytics.trackFormSubmission('Early Access Form', false);
  } finally {
    isSubmitting.value = false;
  }
};

// Initialize EmailJS (if using EmailJS)
emailjs.init('_Z2fOrxU0df4A1n8i')
</script>
